/**
 * The cards of the dashboard command center.
 *
 * Each one owns its loading, empty and error state, reads real records from the API and links to
 * the page where the work actually happens. Nothing is padded out: if a feed is empty the card
 * says so and offers the next action.
 */
import type { ReactNode } from 'react';
import type { ActiveLab, ActiveProject, ActiveScenario, CertificationReadiness, ChecklistItem, DevelopmentOverview, Recommendation, SkillMasteryStatus, WeakArea } from '../../api/development';
import type { Assignment, Notification, ProgramView } from '../../api/types';
import { errorMessage, workplace } from '../../api/client';
import { href } from '../../router';
import { ErrorNotice, formatDay, humanize, pct } from '../../components/common';
import { EmptyState, Icon, ProgressRing, Skeleton, useToast, type IconName } from '../../components/ui';
import { foundationsSummary } from '../../lib/skills';
import { ACHIEVEMENT_NOTICE, computeAchievements, nextAchievements, recentAchievements, type AchievementFacts } from '../../lib/achievements';
import { relativeTime } from '../../lib/activity';
import { minutesCopy, percent, readinessTone, recommendationMeta, recommendationRoute, statusBadgeClass, statusBreakdown, statusIcon, statusLabel } from '../../lib/development';
import { coach, type CoachFeedbackAction, type CoachSuggestionsResponse } from '../../api/coach';
import { becausePreview, confidenceWords, suggestionKindMeta, suggestionRoute } from '../../lib/coach';
import '../../styles/pages/coach.css';

const STATUS_TONE: Record<Assignment['status'], string> = { assigned: 'accent', 'in-progress': 'warn', submitted: 'cyan', reviewed: 'ok' };
const STATUS_LABEL: Record<Assignment['status'], string> = { assigned: 'Assigned', 'in-progress': 'In progress', submitted: 'Submitted', reviewed: 'Reviewed' };

/** A dashboard card: one icon, one heading, an optional link out, and its own loading state. */
export function Card({ icon, tone = 'accent', title, id, more, moreLabel = 'See all', index = 0, loading, error, onRetry, children }: { icon: IconName; tone?: string; title: string; id: string; more?: string; moreLabel?: string; index?: number; loading?: boolean; error?: string; onRetry?: () => void; children: ReactNode }) {
  return (
    <section className="card reveal" style={{ ['--i' as string]: Math.min(index, 8) }} aria-labelledby={id}>
      <div className="card-head">
        <span className={`icon-chip ${tone}`} aria-hidden="true"><Icon name={icon} size={18} /></span>
        <h2 id={id}>{title}</h2>
        {more && <a className="more" href={more}>{moreLabel} <Icon name="arrow-right" size={14} /></a>}
      </div>
      <ErrorNotice message={error} onRetry={onRetry} />
      {loading && !error ? (
        <div aria-hidden="true"><Skeleton kind="text" width="70%" /><Skeleton kind="text" count={2} /></div>
      ) : (
        children
      )}
    </section>
  );
}

/* ------------------------------------------------------------- next action */

/** The top recommendation, with the reason the engine gave for it. */
export function NextBestAction({ rec, career, loading, error, onRetry }: { rec?: Recommendation | null; career?: string; loading: boolean; error?: string; onRetry: () => void }) {
  const meta = rec ? recommendationMeta(rec.kind) : undefined;
  return (
    <section className="card next-action next-best reveal" aria-labelledby="next-title">
      <div className="card-head">
        <span className="icon-chip" aria-hidden="true"><Icon name="target" size={18} /></span>
        <h2 id="next-title">Next best action</h2>
      </div>
      <ErrorNotice message={error} onRetry={onRetry} />
      {loading && !rec && !error && <><Skeleton kind="title" /><Skeleton kind="text" count={2} /></>}
      {!loading && !rec && !error && (
        <EmptyState icon="check" title="Nothing is queued right now" actions={<a className="button small" href={href({ page: 'learn' })}>Browse Learn</a>}>
          You have finished everything the engine can suggest for this track. Pick something from Learn, or ask your manager for an assignment.
        </EmptyState>
      )}
      {rec && meta && (
        <>
          <span className="badge accent"><Icon name={meta.icon} size={12} aria-hidden="true" /> {meta.label}</span>
          <h3>{rec.title}</h3>
          <p className="why">{rec.reason}</p>
          <div className="actions" style={{ margin: 0 }}>
            <a className="button large" href={href(recommendationRoute(rec.kind, rec.id, rec.careerId ? String(rec.careerId) : career))}>
              <Icon name="arrow-right" size={18} aria-hidden="true" /> {meta.action}
            </a>
            {rec.minutes !== undefined && <span className="small muted">{minutesCopy(rec.minutes)}</span>}
          </div>
        </>
      )}
    </section>
  );
}

/** The recommendations after the first, each with its reason. */
export function MoreRecommendations({ recs, career, loading }: { recs: Recommendation[]; career?: string; loading: boolean }) {
  if (loading && recs.length === 0) return null;
  if (recs.length === 0) return null;
  return (
    <section className="card reveal" aria-labelledby="more-recs-title" style={{ marginTop: 'var(--sp-4)' }}>
      <div className="card-head">
        <span className="icon-chip violet" aria-hidden="true"><Icon name="lightbulb" size={18} /></span>
        <h2 id="more-recs-title">Other things worth doing</h2>
        <a className="more" href={href({ page: 'progress', tab: 'stages' })}>Your progress <Icon name="arrow-right" size={14} /></a>
      </div>
      <ul className="rec-list">
        {recs.map((r) => {
          const meta = recommendationMeta(r.kind);
          return (
            <li key={`${r.kind}-${r.id}`}>
              <span className="icon-chip neutral" aria-hidden="true"><Icon name={meta.icon} size={16} /></span>
              <span className="body">
                <a href={href(recommendationRoute(r.kind, r.id, r.careerId ? String(r.careerId) : career))}>{r.title}</a>
                <span className="small muted">{r.reason}</span>
                {r.minutes !== undefined && <span className="xs muted">{minutesCopy(r.minutes)}</span>}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/* ------------------------------------------------------------- active work */

export function ActiveWorkCard({ overview, scenarioTitle, labTitle, projectTitle, loading, index }: { overview?: DevelopmentOverview; scenarioTitle: (id: string) => string; labTitle: (id: string) => string; projectTitle: (id: string) => string; loading: boolean; index: number }) {
  const scenarios: ActiveScenario[] = overview?.activeScenarios ?? [];
  const labs: ActiveLab[] = overview?.activeLabs ?? [];
  const projects: ActiveProject[] = overview?.activeProjects ?? [];
  const total = scenarios.length + labs.length + projects.length;
  return (
    <Card icon="play" title="Active work" id="active-title" more={href({ page: 'assignments' })} moreLabel="Assignments" index={index} loading={loading && !overview}>
      {total === 0 ? (
        <EmptyState icon="play" title="Nothing open right now" actions={<a className="button small" href={href({ page: 'assignments' })}>Find something to work on</a>}>
          Labs, projects and scenarios you have started but not finished are listed here so you can pick them up.
        </EmptyState>
      ) : (
        <ul className="mini-list">
          {scenarios.slice(0, 3).map((s) => (
            <li key={s.id}>
              <span className="icon-chip neutral" aria-hidden="true"><Icon name="briefcase" size={16} /></span>
              <span className="body">
                <strong>{scenarioTitle(s.scenarioId)}</strong>
                <span className="small">Scenario · {s.phase} · started {relativeTime(s.startedAt)}</span>
              </span>
              <a className="button quiet small" href={href({ page: 'scenario', id: s.scenarioId, session: s.id })}>Resume</a>
            </li>
          ))}
          {labs.slice(0, 3).map((l) => (
            <li key={l.id}>
              <span className="icon-chip neutral" aria-hidden="true"><Icon name="terminal" size={16} /></span>
              <span className="body">
                <strong>{labTitle(l.labId)}</strong>
                <span className="small">Lab · {l.mode} mode · started {relativeTime(l.startedAt)}</span>
              </span>
              <a className="button quiet small" href={href({ page: 'learn-lab-session', id: l.id })}>Resume</a>
            </li>
          ))}
          {projects.slice(0, 3).map((p) => (
            <li key={p.id}>
              <span className="icon-chip neutral" aria-hidden="true"><Icon name="folder" size={16} /></span>
              <span className="body">
                <strong>{projectTitle(p.projectId)}</strong>
                <span className="small">Project · {p.mode} · started {relativeTime(p.startedAt)}</span>
              </span>
              <a className="button quiet small" href={href({ page: 'project-session', id: p.id })}>Resume</a>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/* -------------------------------------------------------- learning progress */

export function LearningProgressCard({ overview, careerId, loading, index }: { overview?: DevelopmentOverview; careerId: string; loading: boolean; index: number }) {
  const p = overview?.progress;
  const parts = p ? [p.lessons, p.labs, p.scenarios, p.projects] : [];
  const done = parts.reduce((s, x) => s + x.done, 0);
  const total = parts.reduce((s, x) => s + x.total, 0);
  const ratio = total > 0 ? done / total : 0;
  const rows: { label: string; pair: { done: number; total: number } }[] = p
    ? [
        { label: 'Lessons', pair: p.lessons },
        { label: 'Labs', pair: p.labs },
        { label: 'Scenarios', pair: p.scenarios },
        { label: 'Projects', pair: p.projects },
      ]
    : [];
  return (
    <Card icon="book" tone="cyan" title="Learning progress" id="learning-card-title" more={href({ page: 'learn-path', id: careerId })} moreLabel="Open the path" index={index} loading={loading && !overview}>
      {p && total === 0 ? (
        <EmptyState icon="book" title="No path published for this track yet" actions={<a className="button small" href={href({ page: 'learn' })}>Browse Learn</a>}>
          Lessons, labs and scenarios appear here once a learning path is published.
        </EmptyState>
      ) : (
        p && (
          <>
            <p className="small muted"><span className="tabular">{percent(ratio)}%</span> of your path is done — {done} of {total} items.</p>
            <div className="meter" role="img" aria-label={`Learning path ${percent(ratio)} percent complete`}><span style={{ width: `${percent(ratio)}%` }} /></div>
            <ul className="pair-list" style={{ marginTop: 'var(--sp-3)' }}>
              {rows.map((r) => (
                <li key={r.label}>
                  <div className="row between">
                    <span className="label">{r.label}</span>
                    <span className="count">{r.pair.done}/{r.pair.total}</span>
                  </div>
                  <div className="meter" role="img" aria-label={`${r.label}: ${r.pair.done} of ${r.pair.total} done`}>
                    <span style={{ width: `${r.pair.total > 0 ? percent(r.pair.done / r.pair.total) : 0}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          </>
        )
      )}
    </Card>
  );
}

/* ------------------------------------------------------------ skill mastery */

export function SkillMasteryCard({ overview, loading, index }: { overview?: DevelopmentOverview; loading: boolean; index: number }) {
  const counts = overview?.skillCounts;
  const breakdown = statusBreakdown(counts ?? {});
  const total = breakdown.reduce((s, b) => s + b.count, 0);
  return (
    <Card icon="chart" tone="violet" title="Skill mastery" id="mastery-title" more={href({ page: 'progress', tab: 'skills' })} moreLabel="All skills" index={index} loading={loading && !overview}>
      {counts && total === 0 ? (
        <EmptyState icon="chart" title="No skills recorded yet" actions={<a className="button small" href={href({ page: 'learn' })}>Start a lesson</a>}>
          Every skill on your track appears here once the matrix has something to say about it.
        </EmptyState>
      ) : (
        counts && (
          <>
            <div className="mini-bar" role="img" aria-label={breakdown.filter((b) => b.count > 0).map((b) => `${b.count} ${b.meta.label}`).join(', ')}>
              {breakdown.filter((b) => b.count > 0).map((b) => <span key={b.status} className={`seg tone-${b.meta.tone}`} style={{ width: `${percent(b.share)}%` }} />)}
            </div>
            <ul className="count-list">
              {breakdown.filter((b) => b.count > 0).map((b) => (
                <li key={b.status}>
                  <span className={statusBadgeClass(b.status)}><Icon name={statusIcon(b.status)} size={12} aria-hidden="true" /> {statusLabel(b.status)}</span>
                  <span className="tabular">{b.count}</span>
                </li>
              ))}
            </ul>
            <p className="xs muted" style={{ marginTop: 'var(--sp-3)' }}>{total} skill{total === 1 ? '' : 's'} on this track. Statuses come from scored evidence, not from time spent.</p>
          </>
        )
      )}
    </Card>
  );
}

/* ------------------------------------------------------------- foundations */

/**
 * The foundations pathway at a glance: how many of the "Must know before you work in tech" skills
 * the learner's own work shows at demonstrated or better, with a link to the pathway map.
 */
export function FoundationsCard({ steps, loading, error, onRetry, index }: { steps?: { state?: SkillMasteryStatus }[]; loading: boolean; error?: string; onRetry: () => void; index: number }) {
  const summary = foundationsSummary(steps ?? []);
  return (
    <Card icon="flag" tone="ok" title="Foundations" id="foundations-card-title" more={href({ page: 'foundations' })} moreLabel="Open the map" index={index} loading={loading && !steps} error={error} onRetry={onRetry}>
      {steps && steps.length === 0 ? (
        <EmptyState icon="flag" title="No foundation skills published yet" actions={<a className="button small" href={href({ page: 'skills' })}>Browse skills</a>}>
          The foundations pathway appears once skills are marked as foundations.
        </EmptyState>
      ) : (
        steps && (
          <div className="row" style={{ gap: 'var(--sp-4)', alignItems: 'center', flexWrap: 'wrap' }}>
            <ProgressRing value={summary.ratio} label="Foundations demonstrated" size={84} tone="ok" />
            <div style={{ flex: '1 1 10rem', minWidth: 0 }}>
              <p className="small" style={{ marginTop: 0 }}>
                <span className="tabular">{summary.done}</span> of <span className="tabular">{summary.total}</span> foundation skills at demonstrated or better.
              </p>
              <p className="xs muted" style={{ margin: 0 }}>
                <a href={href({ page: 'foundations' })}>See what to learn first</a> · <a href={href({ page: 'analytics' })}>Your learning analytics</a>
              </p>
            </div>
          </div>
        )
      )}
    </Card>
  );
}

/* --------------------------------------------------------- certification prep */

export function CertificationCard({ prep, dueReviews, certName, loading, index }: { prep?: CertificationReadiness[]; dueReviews?: number; certName: (id: string) => string; loading: boolean; index: number }) {
  const list = prep ?? [];
  return (
    <Card icon="shield" tone="ok" title="Certification prep" id="cert-title" more={href({ page: 'prep' })} moreLabel="Open prep" index={index} loading={loading && !prep}>
      {prep && list.length === 0 ? (
        <EmptyState icon="shield" title="No certification map for this track" actions={<a className="button small" href={href({ page: 'certifications' })}>Browse certifications</a>}>
          Tracks aligned to a certification show a practice readiness indicator here.
        </EmptyState>
      ) : (
        <>
          <ul className="pair-list">
            {list.slice(0, 3).map((c) => (
              <li key={String(c.certificationId)}>
                <div className="row between">
                  <span className="label">{certName(String(c.certificationId))}</span>
                  <span className={`badge ${readinessTone(c.label) === 'neutral' ? '' : readinessTone(c.label)}`}>{c.label}</span>
                </div>
                <div className="meter" role="img" aria-label={`${certName(String(c.certificationId))} practice indicator ${percent(c.score)} percent`}><span style={{ width: `${percent(c.score)}%` }} /></div>
                <span className="xs muted">{c.attempts} practice session{c.attempts === 1 ? '' : 's'}{c.dueReviews > 0 ? ` · ${c.dueReviews} review${c.dueReviews === 1 ? '' : 's'} due` : ''} · <a href={href({ page: 'prep-bank', id: String(c.certificationId) })}>practice</a></span>
              </li>
            ))}
          </ul>
          {dueReviews !== undefined && dueReviews > 0 && (
            <p className="small" style={{ marginTop: 'var(--sp-3)' }}>
              <Icon name="clock" size={14} aria-hidden="true" /> {dueReviews} item{dueReviews === 1 ? '' : 's'} due for review today.{' '}
              {list[0] && <a href={href({ page: 'prep-flashcards', id: String(list[0].certificationId) })}>Open flashcards</a>}
            </p>
          )}
          <p className="xs muted">A readiness label describes your practice here. It does not predict an exam result.</p>
        </>
      )}
    </Card>
  );
}

/* ---------------------------------------------------------------- workplace */

export function WorkplaceCard({ assignments, notifications, managerName, loading, error, onRetry, onChanged, index, children }: { assignments?: Assignment[]; notifications?: Notification[]; managerName?: string; loading: boolean; error?: string; onRetry: () => void; onChanged: () => void; index: number; children?: ReactNode }) {
  const { toast } = useToast();
  const open = (assignments ?? []).filter((a) => a.status !== 'reviewed').slice(0, 3);
  const unread = (notifications ?? []).filter((n) => !n.readAt);
  const shownNotes = [...(notifications ?? [])].sort((a, b) => (a.readAt ? 1 : 0) - (b.readAt ? 1 : 0) || b.createdAt.localeCompare(a.createdAt)).slice(0, 3);
  const markRead = async (id: string) => {
    try {
      await workplace.markRead(id);
      onChanged();
    } catch (e) {
      toast('Could not mark that as read', { body: errorMessage(e), tone: 'bad' });
    }
  };
  return (
    <Card icon="briefcase" title="Workplace" id="workplace-title" more={href({ page: 'workplace' })} moreLabel="Open workplace" index={index} loading={loading && !assignments} error={error} onRetry={onRetry}>
      <h3 style={{ fontSize: 'var(--fs-sm)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)', margin: '0 0 var(--sp-2)' }}>Assignments</h3>
      {open.length === 0 ? (
        <p className="small muted">No open assignments. <a href={href({ page: 'assignments' })}>Ask your manager</a> for the next one.</p>
      ) : (
        <ul className="mini-list">
          {open.map((a) => (
            <li key={a.id}>
              <span className="icon-chip neutral" aria-hidden="true"><Icon name="clipboard" size={16} /></span>
              <span className="body">
                <strong>{a.title}</strong>
                <span className="small">{managerName ? `From ${managerName}` : 'From your manager'} · {relativeTime(a.createdAt)}</span>
              </span>
              <span className={`badge ${STATUS_TONE[a.status]}`}>{STATUS_LABEL[a.status]}</span>
            </li>
          ))}
        </ul>
      )}

      <h3 style={{ fontSize: 'var(--fs-sm)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)', margin: 'var(--sp-4) 0 var(--sp-2)' }}>Messages</h3>
      <p className="small muted" aria-live="polite">{shownNotes.length === 0 ? 'Nothing in your inbox yet.' : unread.length === 0 ? 'Everything read.' : `${unread.length} unread`}</p>
      {shownNotes.length > 0 && (
        <ul className="mini-list">
          {shownNotes.map((n) => (
            <li key={n.id} className={n.readAt ? '' : 'unread'}>
              <span className="icon-chip neutral" aria-hidden="true"><Icon name={n.kind === 'assignment' ? 'clipboard' : 'chat'} size={16} /></span>
              <span className="body">
                <strong>{n.title}</strong>
                <span className="small">{n.body}</span>
                <span className="row" style={{ gap: 'var(--sp-2)', marginTop: '0.35rem' }}>
                  <span className="xs muted">{relativeTime(n.createdAt)}</span>
                  {!n.readAt && <button type="button" className="quiet small" onClick={() => void markRead(n.id)}>Mark read</button>}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
      {children}
    </Card>
  );
}

/* ------------------------------------------------------------ portfolio */

export function PortfolioCard({ count, checklist, loading, index }: { count?: number; checklist?: ChecklistItem[]; loading: boolean; index: number }) {
  const items = checklist ?? [];
  const done = items.filter((i) => i.done).length;
  return (
    <Card icon="folder" tone="cyan" title="Portfolio evidence" id="portfolio-title" more={href({ page: 'deliverables' })} moreLabel="Open portfolio" index={index} loading={loading && count === undefined}>
      {count === 0 ? (
        <EmptyState icon="folder" title="No packaged evidence yet" actions={<a className="button small" href={href({ page: 'projects' })}>Start a project</a>}>
          Completed scenarios, labs and projects can be packaged as portfolio items with their evidence attached.
        </EmptyState>
      ) : (
        <p className="small"><span className="tabular" style={{ fontWeight: 700 }}>{count}</span> item{count === 1 ? '' : 's'} included in your portfolio.</p>
      )}
      {items.length > 0 && (
        <>
          <h3 style={{ fontSize: 'var(--fs-sm)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)', margin: 'var(--sp-3) 0 var(--sp-2)' }}>Resume checklist — {done} of {items.length} done</h3>
          <ul className="checklist">
            {items.map((i) => (
              <li key={i.id} className={i.done ? 'done' : ''}>
                <span className={`check-mark ${i.done ? 'met' : 'unmet'}`} aria-hidden="true"><Icon name={i.done ? 'check' : 'close'} size={13} /></span>
                <span>{i.label}{i.done ? ' — done' : ''}</span>
              </li>
            ))}
          </ul>
          <div className="actions" style={{ marginTop: 'var(--sp-3)' }}>
            <a className="button secondary small" href={href({ page: 'resume' })}>Open the resume builder</a>
          </div>
        </>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------ weak areas */

export function WeakAreasCard({ areas, career, loading, index }: { areas?: WeakArea[]; career: string; loading: boolean; index: number }) {
  const list = areas ?? [];
  return (
    <Card icon="target" tone="warn" title="Weak areas" id="weak-title" more={href({ page: 'progress', tab: 'skills' })} moreLabel="Skill matrix" index={index} loading={loading && !areas}>
      {areas && list.length === 0 ? (
        <EmptyState icon="check" title="Nothing flagged for review" actions={<a className="button small" href={href({ page: 'progress', tab: 'skills' })}>See every skill</a>}>
          Skills appear here when the evidence goes stale or an attempt falls short.
        </EmptyState>
      ) : (
        <ul className="mini-list">
          {list.map((a) => (
            <li key={String(a.skillId)}>
              <span className="icon-chip neutral" aria-hidden="true"><Icon name={statusIcon(a.status)} size={16} /></span>
              <span className="body">
                <strong>{a.name}</strong>
                <span className="small">
                  <span className={statusBadgeClass(a.status)}>{statusLabel(a.status)}</span> · best evidence {pct(a.mastery)}
                </span>
                {a.recommendedNext && (
                  <a className="small" href={href(recommendationRoute(a.recommendedNext.kind, a.recommendedNext.id, career))}>
                    {recommendationMeta(a.recommendedNext.kind).label}: {a.recommendedNext.title}
                  </a>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/* ---------------------------------------------------------- achievements */

export function MilestonesCard({ facts, loading, index }: { facts: AchievementFacts; loading: boolean; index: number }) {
  const all = computeAchievements(facts);
  const recent = recentAchievements(all, 3);
  const next = nextAchievements(all, 2);
  return (
    <Card icon="trophy" tone="ok" title="Recent achievements" id="milestone-title" more={href({ page: 'progress', tab: 'stages' })} moreLabel="Your progress" index={index} loading={loading}>
      {recent.length === 0 ? (
        <EmptyState icon="trophy" title="No milestones yet" actions={<a className="button small" href={href({ page: 'assignments' })}>Find your first task</a>}>
          Milestones record work you have actually finished — a first scenario, a first ticket resolved, a lab passed.
        </EmptyState>
      ) : (
        <div className="milestones">
          {recent.map((a, i) => (
            <div className="milestone earned pop-in" style={{ animationDelay: `${i * 90}ms` }} key={a.id}>
              <span className="icon-chip ok" aria-hidden="true"><Icon name={a.icon} size={18} /></span>
              <span>
                <span className="title">{a.title}</span>
                <span className="small muted">{a.description}</span>
                {a.unlockedAt && <span className="xs muted"> {relativeTime(a.unlockedAt)}</span>}
              </span>
            </div>
          ))}
        </div>
      )}
      {next.length > 0 && (
        <>
          <h3 style={{ fontSize: 'var(--fs-sm)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)', marginTop: 'var(--sp-4)' }}>Next milestones</h3>
          <div className="milestones">
            {next.map((a) => (
              <div className="milestone" key={a.id}>
                <span className="icon-chip neutral" aria-hidden="true"><Icon name={a.icon} size={18} /></span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className="title">{a.title}</span>
                  <span className="small muted">{a.howTo}</span>
                  {a.progress !== undefined && (
                    <span style={{ display: 'block', marginTop: '0.4rem' }}>
                      <span className="meter" role="img" aria-label={`${a.title} ${percent(a.progress)} percent`}><span style={{ width: `${percent(a.progress)}%` }} /></span>
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
      <p className="xs muted" style={{ marginTop: 'var(--sp-3)' }}>{ACHIEVEMENT_NOTICE}</p>
    </Card>
  );
}

/* ------------------------------------------------------- upcoming reviews */

export function ReviewsCard({ dueReviews, prep, certName, loading, index }: { dueReviews?: number; prep?: CertificationReadiness[]; certName: (id: string) => string; loading: boolean; index: number }) {
  const due = dueReviews ?? 0;
  const first = (prep ?? [])[0];
  return (
    <Card icon="clock" tone="warn" title="Upcoming reviews" id="reviews-title" more={href({ page: 'prep' })} moreLabel="Open prep" index={index} loading={loading && dueReviews === undefined}>
      {due === 0 ? (
        <EmptyState icon="check" title="Nothing due today" actions={first ? <a className="button small" href={href({ page: 'prep-flashcards', id: String(first.certificationId) })}>Practice anyway</a> : <a className="button small" href={href({ page: 'prep' })}>Open prep</a>}>
          Spaced review schedules a card for the day it is most useful. Nothing is waiting right now.
        </EmptyState>
      ) : (
        <>
          <p className="small"><span className="tabular" style={{ fontWeight: 700 }}>{due}</span> item{due === 1 ? '' : 's'} due for review today.</p>
          <ul className="pair-list">
            {(prep ?? []).filter((c) => c.dueReviews > 0).map((c) => (
              <li key={String(c.certificationId)}>
                <div className="row between">
                  <span className="label">{certName(String(c.certificationId))}</span>
                  <span className="count">{c.dueReviews} due</span>
                </div>
              </li>
            ))}
          </ul>
          {first && <div className="actions" style={{ marginTop: 'var(--sp-3)' }}><a className="button small" href={href({ page: 'prep-flashcards', id: String(first.certificationId) })}>Open flashcards</a></div>}
        </>
      )}
    </Card>
  );
}

/* ----------------------------------------------------------------- program */

export function ProgramCard({ program, loading, index }: { program?: ProgramView; loading: boolean; index: number }) {
  return (
    <Card icon="calendar" title="Your 30-day program" id="program-title" more={href({ page: 'program' })} moreLabel="Open program" index={index} loading={loading}>
      {!program ? (
        <EmptyState icon="calendar" title="Your program has not started" actions={<a className="button small" href={href({ page: 'program' })}>Start day 1</a>}>
          The program sequences a month of practice, one day at a time. You can start it whenever you are ready.
        </EmptyState>
      ) : (
        <>
          <p className="small">Day <span className="tabular" style={{ fontWeight: 700 }}>{program.run.day}</span> of 30 · {program.progress.done} of {program.progress.total} tasks done · started {formatDay(program.run.startedAt)}</p>
          <div className="meter" role="img" aria-label={`Program ${percent(program.progress.total > 0 ? program.progress.done / program.progress.total : 0)} percent of tasks done`}>
            <span style={{ width: `${program.progress.total > 0 ? percent(program.progress.done / program.progress.total) : 0}%` }} />
          </div>
        </>
      )}
    </Card>
  );
}

/* -------------------------------------------------------------- your tracks */

export function TracksCard({ overview, careerLabel, loading, index }: { overview?: DevelopmentOverview; careerLabel: (id: string) => string; loading: boolean; index: number }) {
  const rows = overview?.enrollments ?? [];
  return (
    <Card icon="compass" tone="violet" title="Your tracks" id="tracks-card-title" more={href({ page: 'progress', tab: 'tracks' })} moreLabel="Manage tracks" index={index} loading={loading && !overview}>
      {rows.length === 0 ? (
        <EmptyState icon="compass" title="No tracks enrolled" actions={<a className="button small" href={href({ page: 'explore' })}>Browse tracks</a>}>
          Enrolling in a track gives it a learning path, a stage journey and its own record.
        </EmptyState>
      ) : (
        <ul className="mini-list">
          {rows.map((e) => (
            <li key={String(e.careerId)}>
              <span className="icon-chip neutral" aria-hidden="true"><Icon name="map" size={16} /></span>
              <span className="body">
                <strong>{careerLabel(String(e.careerId))}</strong>
                <span className="small">Enrolled {formatDay(e.enrolledAt)}</span>
              </span>
              {e.primary && <span className="badge accent">Primary</span>}
              <span className="badge">{humanize(e.status)}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/* -------------------------------------------------------------- learning coach */

/** The coach's top suggestion with its first reasons, the full reasoning one click away, and accept or dismiss. */
/** The curriculum's next skill for the learner's track, shown as one line under the coach card. */
export interface NextSkillLine {
  careerName: string;
  skillId: string;
  name: string;
  /** Its guide when the skill has one, otherwise its skill page. */
  to: string;
}

export function CoachCard({ data, loading, error, onRetry, onChanged, index, nextSkill }: { data?: CoachSuggestionsResponse; loading: boolean; error?: string; onRetry: () => void; onChanged: () => void; index: number; nextSkill?: NextSkillLine }) {
  const { toast } = useToast();
  const top = data?.suggestions[0];
  const send = async (action: CoachFeedbackAction) => {
    if (!top) return;
    try {
      await coach.feedback(top, action);
      if (action === 'dismissed') {
        toast('Hidden for a week', { tone: 'ok', body: 'The coach will suggest this kind of thing a little less.' });
        onChanged();
      }
    } catch (e) {
      if (action === 'dismissed') toast('The coach could not save that', { tone: 'bad', body: errorMessage(e) });
    }
  };
  const meta = top ? suggestionKindMeta(top.kind) : undefined;
  const preview = top ? becausePreview(top) : undefined;
  return (
    <Card icon="compass-star" tone="cyan" title="Coach" id="coach-card-title" more={href({ page: 'coach' })} moreLabel="Open the coach" index={index} loading={loading && !data} error={error} onRetry={onRetry}>
      <div className="coach-card">
        {!top || !meta || !preview ? (
          data && data.modelSummary.eventCount === 0 ? (
            <EmptyState icon="compass-star" title="Do one lesson and the coach starts learning" actions={<a className="button small" href={href({ page: 'learn' })}>Browse lessons</a>}>
              Suggestions with their reasons appear here once the coach has some of your answers to go on.
            </EmptyState>
          ) : (
            <p className="small muted">No suggestion right now. <a href={href({ page: 'coach', tab: 'ask' })}>Ask the coach a question</a> instead.</p>
          )
        ) : (
          <>
            <div className="suggestion-badges">
              <span className="badge accent"><Icon name={meta.icon} size={12} aria-hidden="true" /> {meta.label}</span>
              <span className="badge">{confidenceWords(top.confidence)}</span>
            </div>
            <h3 id="coach-card-suggestion">{top.title}</h3>
            <ul className="because-lines small">
              {preview.shown.map((b) => <li key={b}>{b}</li>)}
            </ul>
            <details className="because">
              <summary>Why?</summary>
              {preview.more > 0 && (
                <ul className="small">
                  {top.because.slice(preview.shown.length).map((b) => <li key={b}>{b}</li>)}
                </ul>
              )}
              <p className="small"><strong>Therefore:</strong> {top.therefore}</p>
            </details>
            <div className="actions">
              <a className="button small" aria-describedby="coach-card-suggestion" href={href(suggestionRoute(top.kind, top.targetId))} onClick={() => void send('accepted')}>
                <Icon name="arrow-right" size={16} aria-hidden="true" /> {meta.action}
              </a>
              <button type="button" className="quiet small" aria-describedby="coach-card-suggestion" onClick={() => void send('dismissed')}>Not now</button>
            </div>
          </>
        )}
        {nextSkill && (
          <p className="small next-skill-line">
            <Icon name="flag" size={14} aria-hidden="true" /> Your next skill on {nextSkill.careerName}: <a href={nextSkill.to}>{nextSkill.name}</a>
          </p>
        )}
      </div>
    </Card>
  );
}
